/**
 * Tracer Store - Zustand with Persistence
 * Replaces TracerContext for state that needs to persist across navigation
 * 
 * Benefits over React Context:
 * - Automatic persistence to sessionStorage
 * - No unnecessary re-renders
 * - Works outside React components
 * - Simpler API
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { PathfinderDataPoint, PathfinderRoute } from '../services/pathfinderApi';
import { DrawCircle } from '../components/Pathfinder/AreaSearch';
import { AnalysisTab } from '../components/Pathfinder/GeoNeutronDataTable';

// Geocoding cache entry with place_type
export interface GeocodedLocation {
  id: string;
  name: string;
  place_type?: string;
  coordinates: [number, number];
}

export interface TracerStoreState {
  // Core data
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  visibleDataPoints: PathfinderDataPoint[];
  visibleRoutes: PathfinderRoute[];
  
  // Time range
  timeRange: { start: Date; end: Date } | null;
  currentTime: Date;
  
  // Map state
  drawCircles: DrawCircle[];
  mapCenter: [number, number];
  mapZoom: number;
  
  // Selection state
  selectedStudents: string[];
  selectedDevices: Record<string, string[]>;
  actualDevices: Record<string, any[]>;
  
  // Analysis state
  dwellLocations: any[];
  visibleDwells: any[];
  analysisTabs: AnalysisTab[];
  
  // Geocoding settings (shared across Link Analysis & PoL Analysis)
  enablePlaceNames: boolean;
  geocodedLocations: Map<string, GeocodedLocation>; // Cache of geocoded locations
  
  // Link Analysis specific state
  linkAnalysisNodes: any[];
  linkAnalysisLinks: any[];
  linkAnalysisViewMode: 'proximity' | 'shared-locations';
}

export interface TracerStoreActions {
  // Setters
  setDataPoints: (points: PathfinderDataPoint[]) => void;
  setRoutes: (routes: PathfinderRoute[]) => void;
  setVisibleDataPoints: (points: PathfinderDataPoint[]) => void;
  setVisibleRoutes: (routes: PathfinderRoute[]) => void;
  setTimeRange: (range: { start: Date; end: Date } | null) => void;
  setCurrentTime: (time: Date) => void;
  setDrawCircles: (circles: DrawCircle[]) => void;
  setMapCenter: (center: [number, number]) => void;
  setMapZoom: (zoom: number) => void;
  setSelectedStudents: (students: string[]) => void;
  setSelectedDevices: (devices: Record<string, string[]>) => void;
  setActualDevices: (devices: Record<string, any[]>) => void;
  setDwellLocations: (dwells: any[]) => void;
  setVisibleDwells: (dwells: any[]) => void;
  setAnalysisTabs: (tabs: AnalysisTab[]) => void;
  setEnablePlaceNames: (enabled: boolean) => void;
  
  // Geocoding cache
  addGeocodedLocation: (location: GeocodedLocation) => void;
  addGeocodedLocations: (locations: GeocodedLocation[]) => void;
  getGeocodedLocation: (id: string) => GeocodedLocation | undefined;
  
  // Link Analysis
  setLinkAnalysisData: (nodes: any[], links: any[]) => void;
  setLinkAnalysisViewMode: (mode: 'proximity' | 'shared-locations') => void;
  
  // Bulk update
  updateState: (updates: Partial<TracerStoreState>) => void;
  
  // Reset
  resetState: () => void;
}

const initialState: TracerStoreState = {
  dataPoints: [],
  routes: [],
  visibleDataPoints: [],
  visibleRoutes: [],
  timeRange: null,
  currentTime: new Date(),
  drawCircles: [],
  mapCenter: [39.8283, -98.5795], // Default: center of continental US
  mapZoom: 4, // Default: zoom to show continental US
  selectedStudents: [],
  selectedDevices: {},
  actualDevices: {},
  dwellLocations: [],
  visibleDwells: [],
  analysisTabs: [],
  enablePlaceNames: false,
  geocodedLocations: new Map(),
  linkAnalysisNodes: [],
  linkAnalysisLinks: [],
  linkAnalysisViewMode: 'shared-locations',
};

// Helper to convert dates in objects (for rehydration)
const rehydrateDates = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') {
    // Check if it looks like an ISO date string
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(obj)) {
      const date = new Date(obj);
      if (!isNaN(date.getTime())) return date;
    }
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(rehydrateDates);
  }
  if (typeof obj === 'object') {
    const result: any = {};
    for (const key of Object.keys(obj)) {
      result[key] = rehydrateDates(obj[key]);
    }
    return result;
  }
  return obj;
};

// Custom storage with Map serialization and Date rehydration support
const customStorage = {
  getItem: (name: string) => {
    const str = sessionStorage.getItem(name);
    if (!str) return null;
    try {
      const parsed = JSON.parse(str);
      // Convert array back to Map for geocodedLocations
      if (parsed.state?.geocodedLocations) {
        parsed.state.geocodedLocations = new Map(parsed.state.geocodedLocations);
      }
      // Rehydrate Date objects in dataPoints, timeRange, etc.
      if (parsed.state?.dataPoints) {
        parsed.state.dataPoints = rehydrateDates(parsed.state.dataPoints);
      }
      if (parsed.state?.visibleDataPoints) {
        parsed.state.visibleDataPoints = rehydrateDates(parsed.state.visibleDataPoints);
      }
      if (parsed.state?.timeRange) {
        parsed.state.timeRange = rehydrateDates(parsed.state.timeRange);
      }
      if (parsed.state?.currentTime) {
        parsed.state.currentTime = rehydrateDates(parsed.state.currentTime);
      }
      if (parsed.state?.analysisTabs) {
        parsed.state.analysisTabs = rehydrateDates(parsed.state.analysisTabs);
      }
      if (parsed.state?.dwellLocations) {
        parsed.state.dwellLocations = rehydrateDates(parsed.state.dwellLocations);
      }
      return parsed;
    } catch (e) {
      console.error('Error rehydrating tracer store:', e);
      return null;
    }
  },
  setItem: (name: string, value: any) => {
    try {
      // Convert Map to array for serialization
      const toStore = {
        ...value,
        state: {
          ...value.state,
          geocodedLocations: value.state?.geocodedLocations 
            ? Array.from(value.state.geocodedLocations.entries())
            : []
        }
      };
      sessionStorage.setItem(name, JSON.stringify(toStore));
    } catch (e) {
      // Silently handle quota errors - only warn once to avoid console spam
      if (!(window as any).__tracerStoreQuotaWarned) {
        console.warn('[TracerStore] Storage quota exceeded - some state may not persist');
        (window as any).__tracerStoreQuotaWarned = true;
      }
    }
  },
  removeItem: (name: string) => {
    sessionStorage.removeItem(name);
  }
};

export const useTracerStore = create<TracerStoreState & TracerStoreActions>()(
  persist(
    (set, get) => ({
      ...initialState,
      
      // Setters
      setDataPoints: (points) => set({ dataPoints: points }),
      setRoutes: (routes) => set({ routes }),
      setVisibleDataPoints: (points) => set({ visibleDataPoints: points }),
      setVisibleRoutes: (routes) => set({ visibleRoutes: routes }),
      setTimeRange: (range) => set({ timeRange: range }),
      setCurrentTime: (time) => set({ currentTime: time }),
      setDrawCircles: (circles) => set({ drawCircles: circles }),
      setMapCenter: (center) => set({ mapCenter: center }),
      setMapZoom: (zoom) => set({ mapZoom: zoom }),
      setSelectedStudents: (students) => set({ selectedStudents: students }),
      setSelectedDevices: (devices) => set({ selectedDevices: devices }),
      setActualDevices: (devices) => set({ actualDevices: devices }),
      setDwellLocations: (dwells) => set({ dwellLocations: dwells }),
      setVisibleDwells: (dwells) => set({ visibleDwells: dwells }),
      setAnalysisTabs: (tabs) => set({ analysisTabs: tabs }),
      setEnablePlaceNames: (enabled) => set({ enablePlaceNames: enabled }),
      
      // Geocoding cache
      addGeocodedLocation: (location) => set((state) => {
        const newCache = new Map(state.geocodedLocations);
        newCache.set(location.id, location);
        return { geocodedLocations: newCache };
      }),
      
      addGeocodedLocations: (locations) => set((state) => {
        const newCache = new Map(state.geocodedLocations);
        locations.forEach(loc => newCache.set(loc.id, loc));
        return { geocodedLocations: newCache };
      }),
      
      getGeocodedLocation: (id) => get().geocodedLocations.get(id),
      
      // Link Analysis
      setLinkAnalysisData: (nodes, links) => set({ 
        linkAnalysisNodes: nodes, 
        linkAnalysisLinks: links 
      }),
      setLinkAnalysisViewMode: (mode) => set({ linkAnalysisViewMode: mode }),
      
      // Bulk update
      updateState: (updates) => set((state) => ({ ...state, ...updates })),
      
      // Reset
      resetState: () => set(initialState),
    }),
    {
      name: 'tracer-store',
      storage: customStorage,
      // Persist critical state to survive navigation
      // Note: Large datasets (12k+ points) may exceed quota - that's OK, they'll just not persist
      partialize: (state) => ({
        // Core data - CRITICAL for state preservation across navigation
        dataPoints: state.dataPoints,
        routes: state.routes,
        visibleDataPoints: state.visibleDataPoints,
        visibleRoutes: state.visibleRoutes,
        
        // Time state
        timeRange: state.timeRange,
        currentTime: state.currentTime,
        
        // Selection state
        selectedStudents: state.selectedStudents,
        selectedDevices: state.selectedDevices,
        actualDevices: state.actualDevices,
        
        // Analysis state - CRITICAL for tab preservation
        analysisTabs: state.analysisTabs,
        dwellLocations: state.dwellLocations,
        visibleDwells: state.visibleDwells,
        
        // Map state - only persist draw circles, NOT map position (start fresh each session)
        drawCircles: state.drawCircles,
        // Note: mapCenter and mapZoom intentionally NOT persisted - start at default global view
        
        // Geocoding
        geocodedLocations: state.geocodedLocations,
        
        // Link Analysis
        linkAnalysisNodes: state.linkAnalysisNodes,
        linkAnalysisLinks: state.linkAnalysisLinks,
        linkAnalysisViewMode: state.linkAnalysisViewMode,
        
        // Note: enablePlaceNames intentionally NOT persisted - defaults to OFF each session
      }),
    }
  )
);

// Selector hooks for optimized re-renders
export const useEnablePlaceNames = () => useTracerStore((state) => state.enablePlaceNames);
export const useSetEnablePlaceNames = () => useTracerStore((state) => state.setEnablePlaceNames);
export const useGeocodedLocations = () => useTracerStore((state) => state.geocodedLocations);
export const useLinkAnalysisViewMode = () => useTracerStore((state) => state.linkAnalysisViewMode);

